import React, { useState, useEffect } from 'react';
import { formatDate } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  CreditCardIcon,
  ShieldCheckIcon,
  CommandLineIcon,
  DocumentTextIcon,
  ClipboardDocumentIcon,
  UserIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import { getPlanFeatures } from '../utils/planUtils';

export default function Settings() {
  const { user, fetchUser } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [notifications, setNotifications] = useState({
    riskAlerts: true,
    scanCompletion: true,
    weeklySummary: false
  });

  useEffect(() => {
    if (user?.api_key) {
      setApiKey(user.api_key);
    }
  }, [user]);



  const handleUpgrade = async () => {
    try {
      const response = await api.post('/payments/create-checkout-session', { plan: 'pro' });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError('Failed to initiate checkout. Please try again.');
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await api.post('/payments/create-portal-session');
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      setError('Failed to open billing portal. Please try again.');
    }
  };



  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'pro':
        return 'bg-mint-100 text-mint-800 dark:bg-mint-900/20 dark:text-mint-300';
      case 'enterprise':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300';
    }
  };

  const handleNotificationChange = (type) => {
    setNotifications(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
    // TODO: Save to backend when notification API is implemented
  };

  const handleRegenerateApiKey = async () => {
    try {
      const response = await api.post('/auth/regenerate-api-key');
      setApiKey(response.data.api_key);
      setMessage('API Key regenerated successfully');
      fetchUser(); // Refresh user data
    } catch (error) {
      console.error('Error regenerating API key:', error);
      setError('Failed to regenerate API key');
    }
  };

  const handleOpenDocs = async () => {
    try {
      const response = await api.get('/auth/get-docs-url');
      if (response.data.full_url) {
        window.open(response.data.full_url, '_blank');
      }
    } catch (error) {
      console.error('Error getting docs URL:', error);
      if (error.response && error.response.status === 403) {
        setError('API documentation is only available on Pro and Enterprise plans.');
      } else {
        setError('Failed to open API documentation');
      }
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(apiKey);
    setMessage('API Key copied to clipboard');
    setTimeout(() => setMessage(''), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Manage your account preferences and API access
          </p>
        </div>

        {message && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
            <div className="text-sm text-green-700 dark:text-green-300">{message}</div>
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
            <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
          </div>
        )}

        <div className="space-y-6">
          {/* Account Information */}
          <div className="bg-white dark:bg-dark-800 shadow-glow rounded-lg border border-gray-200 dark:border-dark-600">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center mb-4">
                <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Account Information
                </h3>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email Address
                  </label>
                  <div className="mt-1 text-sm text-gray-900 dark:text-white">
                    {user?.email}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Plan
                  </label>
                  <div className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanBadgeColor(user?.plan_type)}`}>
                      {user?.plan_type?.charAt(0).toUpperCase() + user?.plan_type?.slice(1) || 'Free'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Member Since
                  </label>
                  <div className="mt-1 text-sm text-gray-900 dark:text-white">
                    {user?.created_at ? formatDate(user.created_at) : 'N/A'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Status
                  </label>
                  <div className="mt-1 flex items-center">
                    <ShieldCheckIcon className="h-4 w-4 text-green-500 mr-1" />
                    <span className="text-sm text-green-600 dark:text-green-400">Active</span>
                  </div>
                </div>
              </div>
            </div>
          </div>



          {/* Plan & Billing */}
          <div className="bg-white dark:bg-dark-800 shadow-glow rounded-lg border border-gray-200 dark:border-dark-600">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center mb-4">
                <CreditCardIcon className="h-5 w-5 text-gray-400 mr-2" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Plan & Billing
                </h3>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Current Plan: {user?.plan_type?.charAt(0).toUpperCase() + user?.plan_type?.slice(1) || 'Free'}
                    </h4>
                    {user?.plan_type && (
                      <ul className="text-sm text-gray-600 dark:text-gray-400 mt-1 space-y-1">
                        {getPlanFeatures(user.plan_type).features.map((feature, index) => (
                          <li key={index}>• {feature}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {user?.plan_type === 'free' ? (
                    <button
                      onClick={handleUpgrade}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                    >
                      Upgrade Plan
                    </button>
                  ) : (
                    <button
                      onClick={handleManageSubscription}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-dark-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-700 hover:bg-gray-50 dark:hover:bg-dark-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                    >
                      Manage Subscription
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mt-8">
                <div className="text-center p-4 border border-gray-200 dark:border-dark-600 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {user?.domains_count || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Domains</div>
                </div>

                <div className="text-center p-4 border border-gray-200 dark:border-dark-600 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {user?.scans_count || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Total Scans</div>
                </div>

                <div className="text-center p-4 border border-gray-200 dark:border-dark-600 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {user?.findings_count || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Findings</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* API Access */}
        <div className="bg-white dark:bg-dark-800 shadow-glow rounded-lg mt-6 mb-6 border border-gray-200 dark:border-dark-600">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <CommandLineIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                API Access
              </h3>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Use your API Key to integrate TypoGuard with your external tools and workflows.
                API access allows different rate limits based on your plan.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Your API Key
                </label>
                <div className="mt-1 relative rounded-md shadow-sm max-w-md">
                  <input
                    type="text"
                    readOnly
                    value={user?.plan_type === 'free' ? 'Upgrade to Pro to access API' : apiKey}
                    onClick={user?.plan_type === 'free' ? undefined : copyToClipboard}
                    className={`block w-full pr-10 sm:text-sm rounded-md border-gray-300 dark:border-dark-600 dark:bg-dark-700 dark:text-gray-300 focus:ring-mint-500 focus:border-mint-500 ${user?.plan_type !== 'free' ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-600' : 'cursor-not-allowed opacity-75'}`}
                    title={user?.plan_type !== 'free' ? "Click to copy API Key" : "Upgrade to access"}
                  />
                  {user?.plan_type !== 'free' && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <ClipboardDocumentIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </div>
                  )}
                </div>
                {user?.plan_type !== 'free' && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Click the field to copy your API key
                  </p>
                )}
              </div>

              <div className="flex space-x-4 pt-2">
                <button
                  type="button"
                  onClick={handleRegenerateApiKey}
                  disabled={user?.plan_type === 'free'}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500 disabled:opacity-50"
                >
                  Regenerate Key
                </button>

                <button
                  type="button"
                  onClick={handleOpenDocs}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-mint-700 bg-mint-100 hover:bg-mint-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                >
                  <DocumentTextIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
                  View API Documentation
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white dark:bg-dark-800 shadow-glow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <BellIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Notification Preferences
              </h3>
            </div>

            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Risk Alerts
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Get notified via email when high-risk typosquats are detected
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={notifications.riskAlerts}
                  onChange={() => handleNotificationChange('riskAlerts')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Scan Completion
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Get notified via email when scans complete with full results summary
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={notifications.scanCompletion}
                  onChange={() => handleNotificationChange('scanCompletion')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Weekly Summary
                    <span className="ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                      Coming Soon
                    </span>
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Receive weekly reports with domain monitoring insights
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={notifications.weeklySummary}
                  disabled
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded opacity-50"
                />
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Legal & Compliance */}
      <div className="bg-white dark:bg-dark-800 shadow-glow rounded-lg mt-6 border border-gray-200 dark:border-dark-600">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center mb-4">
            <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Legal & Compliance
            </h3>
          </div>
          <div className="space-y-4">
            <div className="flex flex-col space-y-2">
              <a
                href="/legal/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 hover:underline"
              >
                Terms of Service
              </a>
              <a
                href="/legal/terms-and-conditions-of-sale"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 hover:underline"
              >
                Terms and Conditions of Sale (CGV)
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
